/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Helmet } from 'react-helmet';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Grid, IconButton, Stack, Typography, Tabs, Tab, TextField } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilState } from 'recoil';
import ErrorFallback from 'components/error-fallback';
import Empty from 'components/empty';
import { riCatalogs } from 'store';
import { riCatalogDialog, riSelectedCatalog } from 'pages/reference-implementation/store';
import styles, { RefImpServices, ServicePage, CssTextField } from 'assets/style/styles';
import { findNested, getModifiedCatalogs } from './utils';

const CatalogService = React.lazy(() => import('pages/reference-implementation/services/CatalogService'));
const ServiceFilter = React.lazy(() => import('pages/reference-implementation/services/filter'));
const CustomizedDialogs = React.lazy(() => import('pages/reference-implementation/components/dialog'));

function RiServices() {
    const [searchText, setSearchText] = React.useState('');
    const [tabSearchText, setTabSearchText] = React.useState('');
    const [riCatlogsData, setRiCatalogsData] = React.useState([]);
    const [cmpRiCatalogs] = useRecoilState(riCatalogs);
    const [isOpen, setIsOpen] = useRecoilState(riCatalogDialog);
    const [selectedCatalog] = useRecoilState(riSelectedCatalog);
    const [tabValue, setTabValue] = React.useState(0);
    const refToTop = React.useRef();

    const filterByCloudTab = (catalogsArray) => {
        if (tabValue === 0) {
            return catalogsArray.filter((catalog) => catalog?.tags?.some((tag) => tag.toUpperCase().includes('AWS')));
        }
        if (tabValue === 1) {
            return catalogsArray.filter((catalog) => catalog?.tags?.every((tag) => !tag.toUpperCase().includes('AWS')));
        }
        return catalogsArray;
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setTabSearchText('');
    };

    React.useEffect(() => {
        let filteredCatalogs = cmpRiCatalogs;

        if (searchText !== '') {
            const find = findNested(cmpRiCatalogs, searchText);
            filteredCatalogs = [...new Set(find)];
        }

        filteredCatalogs = filterByCloudTab(filteredCatalogs);

        if (tabSearchText !== '') {
            const tabFind = findNested(filteredCatalogs, tabSearchText);
            filteredCatalogs = [...new Set(tabFind)];
        }

        setRiCatalogsData(getModifiedCatalogs(filteredCatalogs));
    }, [cmpRiCatalogs, searchText, tabSearchText, tabValue]);

    const findSearched = (val) => {
        if (val === '') {
            let filteredCatalogs = filterByCloudTab(cmpRiCatalogs);
            if (tabSearchText !== '') {
                const tabFind = findNested(filteredCatalogs, tabSearchText);
                filteredCatalogs = [...new Set(tabFind)];
            }
            setRiCatalogsData(getModifiedCatalogs(filteredCatalogs));
        } else {
            const find = findNested(cmpRiCatalogs, val);
            let filteredCatalogs = [...new Set(find)];
            filteredCatalogs = filterByCloudTab(filteredCatalogs);
            if (tabSearchText !== '') {
                const tabFind = findNested(filteredCatalogs, tabSearchText);
                filteredCatalogs = [...new Set(tabFind)];
            }
            setRiCatalogsData(getModifiedCatalogs(filteredCatalogs));
        }
    };

    const filterData = (riServiceData) => {
        let result = cmpRiCatalogs.filter((item) => {
            if (riServiceData['reference-implementation-id'] && riServiceData?.tag) {
                return item['reference-implementation-id'] === riServiceData['reference-implementation-id'] && item?.tags?.includes(riServiceData?.tag);
            }
            if (riServiceData['reference-implementation-id']) {
                return item['reference-implementation-id'] === riServiceData['reference-implementation-id'];
            }
            if (riServiceData?.tag) {
                return item?.tags?.includes(riServiceData?.tag);
            }
            if (!riServiceData['reference-implementation-id'] && !riServiceData?.tag) {
                return cmpRiCatalogs;
            }
            return true;
        });
        result = filterByCloudTab(result);
        setRiCatalogsData(getModifiedCatalogs(result));
    };

    React.useEffect(() => {
        return refToTop.current && refToTop.current.scrollIntoView();
    }, []);

    return (
        <>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Helmet>
                    <title>Solutions - DevX Infra</title>
                </Helmet>
                <Suspense
                    fallback={
                        <Stack sx={styles.loadingStack}>
                            <Empty color="#000000" isLoading />
                        </Stack>
                    }
                >
                    <Stack spacing={2} sx={styles.mainstack} ref={refToTop}>
                        {cmpRiCatalogs?.length > 0 && (
                            <>
                                <Box sx={styles.headerMainWrapper}>
                                    <Box sx={styles.headercontainer} spacing={1} className="contentContainer">
                                        <Typography variant="h6" sx={styles.headingTypo}>
                                            {' '}
                                            Solutions
                                        </Typography>
                                        <Box sx={{ ...styles.actionContainer, alignContent: 'flex-end' }}>
                                            <Box sx={styles.searchbox}>
                                                <CssTextField
                                                    fullWidth
                                                    autoComplete="off"
                                                    label="Search for solutions"
                                                    size="small"
                                                    variant="outlined"
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    value={searchText}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <>
                                                                {searchText && (
                                                                    <IconButton
                                                                        aria-label="Search "
                                                                        sx={styles.clearicon}
                                                                        onClick={() => {
                                                                            setSearchText('');
                                                                        }}
                                                                    >
                                                                        <ClearIcon />
                                                                    </IconButton>
                                                                )}
                                                                <>
                                                                    <IconButton sx={styles.searchiconBtn} onClick={() => findSearched(searchText)}>
                                                                        <SearchIcon sx={styles.searchicon} />
                                                                    </IconButton>
                                                                </>
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                            <ServiceFilter handleFilter={filterData} />
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom: '1px solid rgb(215 229 248)',
                                    }}
                                    className="contentContainer"
                                >
                                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="solution tabs" sx={ServicePage.tabsContainer}>
                                        <Tab label="AWS Solutions" className="aws-solution-tab" />
                                        <Tab label="Azure Solutions" className="azure-solution-tab" />
                                    </Tabs>
                                </Box>

                                <Box display="flex" flexDirection="column" textAlign="center" spacing={4} justifyContent="center">
                                    <Box sx={RefImpServices.mainTextContainer}>
                                        Start Your Project Now! Explore a World of Possibilities with Cloud-Based Pre-Approved Solutions.
                                    </Box>
                                    <Box sx={RefImpServices.subTextContainer}>Utilize Pre-Approved Architecture to Accelerate Your Cloud Enablement Today.</Box>
                                </Box>

                                <Box className="contentContainer">
                                    <Box sx={{ mb: 3 }}>
                                        <TextField
                                            autoComplete="off"
                                            placeholder={`Search in ${['AWS Solutions', 'Azure Solutions'][tabValue]}`}
                                            size="small"
                                            variant="outlined"
                                            onChange={(e) => setTabSearchText(e.target.value)}
                                            value={tabSearchText}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '5px',
                                                    backgroundColor: '#FFFFFF',
                                                    border: '1px solid #e0e0e0',
                                                    paddingLeft: '12px',
                                                    '&:hover': {
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #d0d0d0',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid rgb(215 229 248)',
                                                        boxShadow: 'none',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderRadius: '5px',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    paddingLeft: '8px',
                                                },
                                                '& .MuiInputBase-root': {
                                                    borderRadius: '5px',
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: <SearchIcon sx={{ color: 'rgb(0, 71, 186)', mr: 1, fontSize: '15px' }} />,
                                                endAdornment: (
                                                    <>
                                                        {tabSearchText && (
                                                            <IconButton
                                                                aria-label="Clear tab search"
                                                                size="small"
                                                                onClick={() => {
                                                                    setTabSearchText('');
                                                                }}
                                                                sx={{
                                                                    color: '#666',
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ ...RefImpServices.newGridWrapper, maxWidth: '1160px' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignContent: 'stretch',
                                        }}
                                        justifyContent="center"
                                    >
                                        {riCatlogsData?.length <= 0 && (
                                            <Grid item xs={12}>
                                                <Stack spacing={2} direction="row" justifyContent="center" alignItems="center" sx={styles.loadingStack}>
                                                    <Empty notFound color="#000000" msg="No Solutions Found" />
                                                </Stack>
                                            </Grid>
                                        )}
                                        {riCatlogsData?.length > 0 && (
                                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ maxWidth: '1153px' }}>
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    flexWrap="wrap"
                                                    justifyContent="center"
                                                    sx={{ gap: '11px', mb: '11px' }}
                                                >
                                                    {riCatlogsData?.map((riService, idx) => (
                                                        <Box display="flex" key={idx}>
                                                            <CatalogService service={riService} shownumber={false} idx={idx + 1} color="#CCCCCC" />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Stack>
                    <CustomizedDialogs
                        isOpen={isOpen}
                        setOpen={setIsOpen}
                        rid={selectedCatalog?.['reference-implementation-id']}
                        title={selectedCatalog?.name}
                        archDiagram={selectedCatalog?.archDiagram}
                        provisioningPath={`${selectedCatalog?.provisioningPath}/${selectedCatalog?.['reference-implementation-id']}`}
                        description={selectedCatalog?.description}
                        helpUrl={selectedCatalog?.['help-link']}
                        tags={selectedCatalog?.tags}
                        // archImg={selectedCatalog?.archImg}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
}

export default RiServices;
